import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";
import {
  getShift,
  updateShift,
  createShift,
  getActiveShift,
} from "../../actions/shiftActions";
import { getShiftTypes } from "../../actions/shiftTypeActions";
import { getProject } from "../../actions/projectActions";
import { getCollectiveLabourAgreements } from "../../actions/claActions";

import Form from "./Form";
import { Grid, Typography, LinearProgress } from "@material-ui/core";

import formatPrice from "../../utils/format-price";
import InputElement from "../InputElement";
import {
  FormMode,
  InputElementSize,
  InputElementType,
  ReportRequiredByType,
} from "../../types/enums";

import dateToDutchString from "../../utils/date-to-dutch-string";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { getEmployeeShift } from "../../actions/employeeActions";

const credentials = JSON.parse(localStorage.getItem("credentials"));

const ShiftForm = (props) => {
  const { t } = useTranslation();

  const [optionShiftTypes, setOptionShiftTypes] = useState([]);
  const [optionClas, setOptionClas] = useState([]);

  const [beginDatetime, setBeginDatetime] = useState(moment());
  const [endDatetime, setEndDatetime] = useState(moment());
  const [claId, setClaId] = useState(null);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [numberOfEmployees, setNumberOfEmployees] = useState(0);
  const [breakTimeSubstraction, setBreakTimeSubstraction] = useState(0);
  const [priceForRequesting, setPriceForRequesting] = useState("");
  const [priceForDelivering, setPriceForDelivering] = useState("");
  const [isEmployeeInEducationAllowed, setIsEmployeeInEducationAllowed] =
    useState(false);
  const [isAvailableForOtherCompanies, setIsAvailableForOtherCompanies] =
    useState(true);
  const [reportRequiredBy, setReportRequiredBy] = useState(false);
  const [workDescription, setWorkDescription] = useState("");

  const [minNumberOfEmployees, setMinNumberOfEmployees] = useState(0);
  const [project, setProject] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const isCreateMode = props.mode === FormMode.CREATE;
  const isUpdateMode = props.mode === FormMode.UPDATE;
  const isViewMode = props.mode === FormMode.VIEW;

  const actionName = isCreateMode
    ? "toevoegen"
    : isUpdateMode
      ? "bijwerken"
      : "";

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!isCreateMode) return;
    const highestShiftPrice = parseFloat(
      Math.max.apply(
        null,
        optionShiftTypes
          .filter((s) => shiftTypes.includes(s.id))
          .map((s) =>
            s.companies && s.companies.length > 0
              ? s.companies[0].ShiftTypeCompany.price
              : s.price,
          ),
      ),
    );

    setPriceForRequesting(highestShiftPrice);
    setPriceForDelivering(highestShiftPrice - 3);
  }, [shiftTypes]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const [clas, shiftTypes] = await Promise.all([
        getCollectiveLabourAgreements(),
        props.getShiftTypes([props.companyEmail]),
      ]);

      setOptionClas(clas);
      setOptionShiftTypes(shiftTypes);

      if (isCreateMode) {
        const project = await props.getProject(props.projectId);

        setReportRequiredBy(project.reportRequiredBy);
        setClaId(project.claId);

        if (!credentials.user.isAdmin) return;

        const lengthOfProject = moment(project.endDate).diff(
          moment(project.beginDate),
          "days",
        );

        setIsAvailableForOtherCompanies(lengthOfProject <= 3);
      }

      if (isUpdateMode || isViewMode) {
        const shift = isUpdateMode
          ? await props.getShift(props.shiftId)
          : isViewMode
            ? await props.getActiveShift(props.shiftId)
            : null;

        const employeeShift =
          isViewMode && credentials.user.type === "EMPLOYEE"
            ? await getEmployeeShift(credentials.user.email, props.shiftId)
            : null;

        const beginDatetime =
          (employeeShift &&
            employeeShift.employees[0].CompanyEmployeeShift.startDateTime) ||
          shift.beginDatetime;
        const endDatetime =
          (employeeShift &&
            employeeShift.employees[0].CompanyEmployeeShift.endDateTime) ||
          shift.endDatetime;

        setBeginDatetime(beginDatetime);
        setEndDatetime(endDatetime);
        setClaId(shift.claId);
        setShiftTypes(shift.shiftTypes.map((shiftType) => shiftType.id));
        setMinNumberOfEmployees(shift.numberOfEmployees);
        setNumberOfEmployees(shift.numberOfEmployees);
        setBreakTimeSubstraction(shift.breakTimeSubstraction);
        setPriceForRequesting(shift.priceForRequesting);
        setPriceForDelivering(shift.priceForDelivering);
        setIsEmployeeInEducationAllowed(shift.isEmployeeInEducationAllowed);
        setIsAvailableForOtherCompanies(shift.isAvailableForOtherCompanies);
        setReportRequiredBy(shift.reportRequiredBy);
        setWorkDescription(shift.workDescription);

        setProject(shift.project);
        setEmployees(shift.employees || []);
      }
    } catch (error) {
      setErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    const form = {
      ...(isCreateMode && { projectId: props.projectId }),
      beginDatetime,
      endDatetime,
      shiftTypes,
      numberOfEmployees,
      breakTimeSubstraction,
      isEmployeeInEducationAllowed,
      isAvailableForOtherCompanies,
      reportRequiredBy,
      workDescription,
      priceForRequesting,
      priceForDelivering,
      claId,
    };

    const promise = isCreateMode
      ? props.createShift(form)
      : isUpdateMode
        ? props.updateShift(props.shiftId, form)
        : null;

    promise
      .then(() => {
        if (isCreateMode)
          window.location.href = `/company/${props.companyEmail}/project/${props.projectId}`;
        setSuccessMessage(`Dienst ${actionName} is voltooid.`);
      })
      .catch(setErrorMessage)
      .finally(() => setIsLoading(false));
  };

  const inputs = [
    {
      id: "projectName",
      hidden: !isViewMode,
      type: InputElementType.TEXT,
      label: "Projectnaam",
      state: project ? project.name : "",
    },
    {
      id: "projectAddress",
      hidden: !isViewMode,
      type: InputElementType.TEXT,
      label: "Adres",
      state: project
        ? `${project.street} ${project.streetNumber}, ${project.zipcode} ${project.city}`
        : "",
      ...(project && {
        caption: {
          text: "Bekijk in Google Maps",
          link: `https://www.google.nl/maps/search/${project.street} ${project.streetNumber}, ${project.zipcode} ${project.city}`,
        },
      }),
    },
    {
      id: "beginDatetime",
      label: "Begindatum *",
      type: isViewMode ? InputElementType.TEXT : InputElementType.DATE_TIME,
      state: isViewMode ? dateToDutchString(beginDatetime) : beginDatetime,
      setState: setBeginDatetime,
    },
    {
      id: "endDatetime",
      label: "Einddatum *",
      type: isViewMode ? InputElementType.TEXT : InputElementType.DATE_TIME,
      state: isViewMode ? dateToDutchString(endDatetime) : endDatetime,
      setState: setEndDatetime,
    },
    {
      id: "claId",
      hidden: props.isActiveShift,
      label: "CAO",
      type: InputElementType.SELECT,
      state: claId,
      setState: setClaId,
      size: InputElementSize.S,
      options: optionClas.map((o) => ({ id: o.id, label: o.name })),
    },
    {
      id: "shiftTypes",
      label: "Dienstsoorten *",
      type: InputElementType.SELECT,
      state: shiftTypes,
      setState: setShiftTypes,
      size: InputElementSize.S,
      options: optionShiftTypes.map((o) => ({ id: o.id, label: o.name })),
      multiple: true,
    },
    {
      id: "numberOfEmployees",
      hidden: isViewMode,
      label: "Aantal medewerkers *",
      type: InputElementType.NUMBER,
      state: numberOfEmployees,
      setState: setNumberOfEmployees,
      size: InputElementSize.S,
      min: isUpdateMode
        ? credentials.user.isAdmin
          ? 1
          : minNumberOfEmployees
        : 0,
    },
    {
      id: "breakTimeSubstraction",
      label: "Pauzevermindering (min.) *",
      type: InputElementType.NUMBER,
      state: breakTimeSubstraction,
      setState: setBreakTimeSubstraction,
      size: InputElementSize.S,
      min: 0,
    },
    {
      id: "priceForRequesting",
      hidden: isViewMode || !credentials.user.isAdmin,
      label: "Aanvraagprijs *",
      type: InputElementType.NUMBER,
      state: priceForRequesting,
      setState: setPriceForRequesting,
      size: InputElementSize.S,
      step: 0.01,
      min: 0,
    },
    {
      id: "priceForDelivering",
      hidden: isViewMode || !credentials.user.isAdmin,
      label: "Leverprijs *",
      type: InputElementType.NUMBER,
      state: priceForDelivering,
      setState: setPriceForDelivering,
      size: InputElementSize.S,
      step: 0.01,
      min: 0,
    },
    {
      id: "reportRequiredBy",
      type: isViewMode ? InputElementType.TEXT : InputElementType.SELECT,
      label: "Rapportage verplicht door medewerkers",
      name: "reportRequiredBy",
      state: isViewMode ? t(reportRequiredBy) : reportRequiredBy,
      ...(!isViewMode && {
        setState: setReportRequiredBy,
        options: Object.values(ReportRequiredByType).map((o) => ({
          id: o,
          label: t(o),
        })),
      }),
    },
    {
      id: "isEmployeeInEducationAllowed",
      type: InputElementType.CHECKBOX,
      label: "Is medewerker in opleiding toegestaan?",
      state: isEmployeeInEducationAllowed,
      setState: setIsEmployeeInEducationAllowed,
    },
    {
      hidden: !credentials.user.isAdmin,
      id: "isAvailableForOtherCompanies",
      type: InputElementType.CHECKBOX,
      label: "Is beschikbaar voor (andere) bedrijven?",
      state: isAvailableForOtherCompanies,
      setState: setIsAvailableForOtherCompanies,
    },
    {
      id: "workDescription",
      type: InputElementType.TEXT,
      size: InputElementSize.L,
      label: "Werkbeschrijving *",
      state: workDescription,
      setState: setWorkDescription,
      rows: 5,
    },
  ];

  return (
    <Form
      onSubmit={!isViewMode && handleSubmit}
      submitText={actionName}
      isFetching={isLoading}
      successMessage={successMessage}
      errorMessage={errorMessage}
      mode={props.mode}
    >
      <Grid container spacing={3}>
        {isViewMode && credentials.user.type !== "EMPLOYEE" && (
          <Grid item xs={12} sm={12}>
            <Typography
              component="h2"
              variant="h4line"
              gutterBottom
              align="right"
            >
              {formatPrice(
                props.isActiveShift ? priceForDelivering : priceForRequesting,
              )}{" "}
              p/uur
            </Typography>
          </Grid>
        )}

        {inputs.map((i) => (
          <InputElement key={i.id} item={i} />
        ))}

        {isCreateMode && shiftTypes.length > 0 && (
          <Grid item xs={12} sm={12}>
            <Typography variant="body1" gutterBottom>
              Door op de onderstaande knop <b>aanvragen</b> te klikken gaat u
              akkoord met een betalingsplicht. De prijs bedraagt{" "}
              <b>{formatPrice(priceForRequesting)}</b> per manuur exclusief BTW.
            </Typography>
          </Grid>
        )}

        {isViewMode &&
          !(
            credentials.user.type === "EMPLOYEE" &&
            !credentials.user.isSeniorEmployee
          ) && (
            <Grid item xs={12} sm={12}>
              <Typography variant="subheading" gutterBottom>
                Aantal medewerkers
              </Typography>

              <LinearProgress
                variant="determinate"
                value={(employees.length / numberOfEmployees) * 100}
              />

              <Typography variant="caption" gutterBottom>
                Nog {numberOfEmployees - employees.length} van de{" "}
                {numberOfEmployees} medewerkers nodig.
              </Typography>
            </Grid>
          )}
      </Grid>
    </Form>
  );
};

ShiftForm.propTypes = {
  companyEmail: PropTypes.string,
  projectId: PropTypes.string,
  shiftId: PropTypes.string,
  isActiveShift: PropTypes.bool,
  getActiveShift: PropTypes.func.isRequired,
  getShiftTypes: PropTypes.func.isRequired,
  getProject: PropTypes.func.isRequired,
  getShift: PropTypes.func.isRequired,
  updateShift: PropTypes.func.isRequired,
  createShift: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(Object.values(FormMode)).isRequired,
};

export default connect(null, {
  getActiveShift,
  getShiftTypes,
  getShift,
  updateShift,
  createShift,
  getProject,
})(ShiftForm);



// WEBPACK FOOTER //
// ./src/components/forms/ShiftForm.js